/*
 * useHoverMenu — open a dropdown on hover for mouse users.
 *
 * Shared by the Header menus (Categories, avatar) so they all behave alike.
 * Touch devices report no hover, so they keep the click behaviour and never
 * get a menu that opens from a stray tap.
 *
 * Not exported from hooks/index.ts on purpose: that file is upstream, and
 * leaving it alone keeps upstream merges conflict-free.
 */

import { useEffect, useRef, useState } from 'react';

/** Grace period, so crossing the gap between toggle and panel does not close it. */
const CLOSE_DELAY_MS = 180;

export const canHover = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(hover: hover)').matches;

const useHoverMenu = (onClose?: () => void) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>();

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
  };

  const onMouseEnter = () => {
    if (!canHover()) return;
    cancelClose();
    setOpen(true);
  };

  const onMouseLeave = () => {
    if (!canHover()) return;
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      onClose?.();
    }, CLOSE_DELAY_MS);
  };

  useEffect(() => cancelClose, []);

  return { open, setOpen, hoverProps: { onMouseEnter, onMouseLeave } };
};

export default useHoverMenu;
